import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import PropTypes, { any } from 'prop-types';

import { required } from '../../../../utils/form-validation';
import {
    TitleSwitch,
    DateTimePicker,
} from '../../../Form';
import { divisionFormatTypeIds, teamPaymentTypeIds } from '../../../../utils/eventConstants';
import { ConfirmActionPopup } from '../../../PopUps';

const ChangePartnerDate = (props) => {
    const {
        formApi,
        coreUI,
        disabled,
        hasDivisionWithTeamPayment,
    } = props;

    const { values, error, setValue } = formApi;

    const [showChangePartnerDate, setShowChangePartnerDate] = useState(values?.changePartnerAllowed || false);
    const [maxChangePartnerDate, setMaxChangePartnerDate] = useState();
    const [eventStart, setEventStart] = useState();
    const [regDateStart, setRegDateStart] = useState();
    const [regDateEnd, setRegDateEnd] = useState();
    const [showConfirmationPopup, setShowConfirmationPopup] = useState();

    useEffect(() => {
        if (values?.eventSchedule) {
            const { eventSchedule, maxChangePartnerDateUtc } = values;
            const timeZone = eventSchedule.timeZone || 77;
            const timeZoneObj = coreUI.types.timeZones.find(tz => { return tz.id === parseInt(timeZone); });

            setRegDateStart(moment.utc(eventSchedule.registrationStartUtc).tz(timeZoneObj.value));
            setRegDateEnd(moment.utc(eventSchedule.registrationEndUtc).tz(timeZoneObj.value));
            setEventStart(moment.utc(eventSchedule.eventStartUtc).tz(timeZoneObj.value));
            if (maxChangePartnerDateUtc && !maxChangePartnerDate) {
                setMaxChangePartnerDate(moment.utc(maxChangePartnerDateUtc).tz(timeZoneObj.value));
            }
        }
    }, [values, coreUI.types.timeZones]);

    const handleDateChange = (key, date) => {
        if (key === 'maxChangePartnerDateUtc' && date?.isBefore(eventStart)) {
            setMaxChangePartnerDate(date);
            setValue('changePartnerAllowed', true);
            setValue('maxChangePartnerDateUtc', date.toISOString());
        }
        formApi.validate(formApi.values);
    };

    const toggleChangePartnerDate = () => {
        if (showChangePartnerDate) {
            setShowChangePartnerDate(!showChangePartnerDate);
            setValue('changePartnerAllowed', false);
            setMaxChangePartnerDate();
            formApi.reset('maxChangePartnerDateUtc');
            formApi.validate(formApi.values);
        } else {
            setShowChangePartnerDate(!showChangePartnerDate);
            if (regDateEnd && eventStart && regDateEnd.isBefore(eventStart)) {
                setValue('maxChangePartnerDateUtc', regDateEnd.toISOString());
            }
            setValue('changePartnerAllowed', true);
            values.eventDivisions.forEach((div, i) => {
                if (parseInt(div.divisionFormatType) === divisionFormatTypeIds.DOUBLES) {
                    setValue(['eventDivisions', i, 'teamPaymentTypeId'], teamPaymentTypeIds.PAY_SEPARATELY);
                }
            });
        }
    };

    const toggleChangePartnerDateCheck = () => {
        if (showChangePartnerDate) {
            toggleChangePartnerDate();
        } else if (hasDivisionWithTeamPayment) {
            setShowConfirmationPopup(true);
        } else {
            toggleChangePartnerDate();
        }
    };

    return (
        <div className="col-12">
            <div className={`form-block-content`}>
                <div className="row">
                    <div className="col-12">
                        <div className="div div16" />
                        <TitleSwitch
                            id="changePartnerDate"
                            formGroupCssClass={`mt16`}
                            label={'Allow players to add/change partner'}
                            description={`If enabled, players are allowed to add/change partner without contacting the tournament provider until the specified date. After this date, add/change partner requests can only be initiated by the tournament provider. Note: If this feature is enabled, all doubles divisions will be required to use the 'Pay separately' option.`}
                            value={showChangePartnerDate}
                            onChange={toggleChangePartnerDateCheck}
                            disabled={disabled}
                            data-testid="event-edit.change-partner"
                        />
                    </div>
                </div>
                {showChangePartnerDate && (
                    <div className="row">
                        <div className={`col-12 ${error?.regEnd ? 'is-invalid' : ''}`}>
                            <div className="bg-white p16">
                                <DateTimePicker
                                    label="Max player switch date"
                                    id="maxChangePartnerDate"
                                    field="maxChangePartnerDateUtc"
                                    placeholderText="Select Date"
                                    selected={maxChangePartnerDate}
                                    minDate={regDateStart}
                                    maxDate={eventStart}
                                    handleDateChange={(date) => handleDateChange('maxChangePartnerDateUtc', date)}
                                    validate={showChangePartnerDate && required}
                                    themeV3
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {showConfirmationPopup && (
                <ConfirmActionPopup
                    confirmationTitle={`Confirm settings change`}
                    confirmationMessage={(
                        <>
                            You have created doubles divisions to use the &apos;Pay as a team&apos; payment option.
                            <br /><br />
                            If you enable this feature to allow players to add/change partners, all existing doubles divisions will automatically be updated to the &apos;Pay separately&apos; payment option.
                            <br /><br />
                            Would you like to continue?
                        </>
                    )}
                    confirmButtonLabel={`Yes`}
                    onCloseClick={() => {
                        setShowConfirmationPopup(false);
                    }}
                    onCtaClick={() => {
                        setShowConfirmationPopup(false);
                        toggleChangePartnerDate();
                    }}
                    data-testid="modal.confirm-change-partner-date"
                />
            )}
        </div>
    );
};

ChangePartnerDate.propTypes = {
    formApi: PropTypes.objectOf(any).isRequired,
    coreUI: PropTypes.objectOf(any).isRequired,
    disabled: PropTypes.bool,
    hasDivisionWithTeamPayment: PropTypes.bool,
};

ChangePartnerDate.defaultProps = {
    disabled: false,
    hasDivisionWithTeamPayment: false,
};

function mapStateToProps({ coreUI }) {
    return { coreUI };
}

export default connect(mapStateToProps)(ChangePartnerDate);
